import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { bookingService, creditNoteService, talentService, emailService } from '../../../api/apiService';
import { buildCreditNoteEmailHtml } from './creditNoteEmailTemplate';
import confirmIcon from '../../../images/confirm.svg';
import '../../../App.css';
import './ConfirmedListCreditNotes.css';

/**
 * Confirmed bookings list for credit notes.
 * Shows all confirmed bookings with actions to create credit note(s).
 */
function ConfirmedListCreditNotes() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [createMessage, setCreateMessage] = useState(null);
  const [createErrors, setCreateErrors] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await bookingService.searchBookings({
        status: 'confirmed',
        limit: 500,
        offset: 0
      });
      const data = res.data;
      const list = data?.bookings || data || [];
      setBookings(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error loading confirmed bookings:', err);
      setError(err.message || 'Failed to load confirmed bookings');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Convert a Blob to a base64 string (strips the data-URL prefix)
  const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob instanceof Blob ? blob : new Blob([blob]));
    });

  /**
   * Send credit note notification emails for a list of freshly created credit notes.
   * Follows the same pattern as booking confirmation emails in ListViewConfirmed.js:
   *   - Fetch full credit note to get talent_id
   *   - Fetch talent by ID to get email address (talent.email || talent.E_Mail)
   *   - to_email is an array: [talentEmail, 'talents@set-cast.com']
   *   - PDF and XML attached as base64 / utf-8
   *
   * @param {Array} createdNotes - Items from API `data.created` — each needs at least `id`
   */
  const sendCreditNoteEmails = async (createdNotes) => {
    for (const cnSummary of createdNotes) {
      try {
        // 1. Fetch full credit note to get talent_id and all details for the template
        const cnRes = await creditNoteService.getCreditNoteById(cnSummary.id);
        const cn = cnRes?.data?.creditNote || cnRes?.data || cnSummary;
        const noteNumber = cn.credit_note_number || cn.creditNoteNumber || cn.id;

        // 2. Fetch talent by ID — same as ListViewConfirmed does for booking confirmation
        const talentId = cn.talent_id || cn.talentId;
        if (!talentId) {
          console.warn(`No talent_id on CN ${noteNumber}, skipping email.`);
          continue;
        }

        const talentRes = await talentService.getTalentById(talentId);
        const talent = talentRes?.data?.talent || talentRes?.data;
        const talentEmail = talent?.email || talent?.E_Mail;

        if (!talentEmail) {
          console.warn(`No email on talent ${talentId} for CN ${noteNumber}, skipping email.`);
          continue;
        }

        // 3. Fetch PDF and XML in parallel
        const [pdfRes, xmlRes] = await Promise.all([
          creditNoteService.getCreditNotePdf(cn.id || cnSummary.id),
          creditNoteService.getCreditNoteXml(cn.id || cnSummary.id),
        ]);

        const pdfBase64 = await blobToBase64(pdfRes.data);
        const xmlContent = typeof xmlRes.data === 'string' ? xmlRes.data : String(xmlRes.data);

        // 4. Send — to_email array matches the pattern in ListViewConfirmed
        await emailService.sendEmail({
          to_email: [talentEmail, 'talents@set-cast.com'],
          subject: `Credit Note ${noteNumber} – Set-Cast`,
          content: buildCreditNoteEmailHtml(cn),
          attachments: [
            {
              filename: `Gutschrift_${noteNumber}.pdf`,
              content: pdfBase64,
              encoding: 'base64',
              contentType: 'application/pdf',
            },
            {
              filename: `Gutschrift_${noteNumber}.xml`,
              content: xmlContent,
              encoding: 'utf-8',
              contentType: 'application/xml',
            },
          ],
        });
      } catch (err) {
        console.error(`Email failed for CN ${cnSummary.creditNoteNumber || cnSummary.id}:`, err);
        // Don't throw — continue with remaining notes, same as ListViewConfirmed
      }
    }
  };

  const handleCreateCreditNote = async (booking) => {
    if (booking.credit_note_generated) return;
    setCreateLoading(true);
    setCreateMessage(null);
    setCreateErrors([]);
    try {
      const res = await creditNoteService.createFromBookings([booking.id]);
      const data = res.data;
      const created = data?.created || [];
      const errs = data?.errors || [];
      if (created.length > 0) {
        setCreateMessage(`Credit note ${created[0].creditNoteNumber} created. Sending email…`);
        await load();
        await sendCreditNoteEmails(created);
        setCreateMessage(`Credit note ${created[0].creditNoteNumber} created. Email sent.`);
      }
      if (errs.length > 0) setCreateErrors(errs);
    } catch (err) {
      setCreateErrors([err.response?.data?.message || err.message || 'Failed to create credit note']);
    } finally {
      setCreateLoading(false);
    }
  };

  const withoutNote = bookings.filter((b) => !b.credit_note_generated);
  const toggleSelection = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAllWithoutNote = () => {
    setSelectedIds(new Set(withoutNote.map((b) => b.id)));
  };
  const clearSelection = () => setSelectedIds(new Set());
  const isSelected = (id) => selectedIds.has(id);

  const handleCreateForSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      setCreateMessage('Select at least one booking.');
      return;
    }
    setCreateLoading(true);
    setCreateMessage(null);
    setCreateErrors([]);
    try {
      const res = await creditNoteService.createFromBookings(ids);
      const data = res.data;
      const created = data?.created || [];
      const errs = data?.errors || [];
      if (created.length > 0) {
        setCreateMessage(`${created.length} credit note(s) created (${created.map((c) => c.creditNoteNumber).join(', ')}). Sending emails…`);
        setSelectedIds(new Set());
        await load();
        await sendCreditNoteEmails(created);
        setCreateMessage(`${created.length} credit note(s) created. Emails sent.`);
      }
      if (errs.length > 0) setCreateErrors(errs);
    } catch (err) {
      setCreateErrors([err.response?.data?.message || err.message || 'Failed to create credit notes']);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCreateAllCreditNotes = async () => {
    const withoutNote = bookings.filter(b => !b.credit_note_generated);
    if (withoutNote.length === 0) {
      setCreateMessage('No confirmed bookings without a credit note.');
      return;
    }
    setCreateLoading(true);
    setCreateMessage(null);
    setCreateErrors([]);
    try {
      const res = await creditNoteService.createFromBookings(withoutNote.map(b => b.id));
      const data = res.data;
      const created = data?.created || [];
      const errs = data?.errors || [];
      if (created.length > 0) {
        setCreateMessage(`${created.length} credit note(s) created (${created.map(c => c.creditNoteNumber).join(', ')}). Sending emails…`);
        await load();
        await sendCreditNoteEmails(created);
        setCreateMessage(`${created.length} credit note(s) created. Emails sent.`);
      }
      if (errs.length > 0) setCreateErrors(errs);
    } catch (err) {
      setCreateErrors([err.response?.data?.message || err.message || 'Failed to create credit notes']);
    } finally {
      setCreateLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const date = typeof d === 'string' ? new Date(d) : d;
    return isNaN(date.getTime()) ? d : date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const clientName = (b) => {
    if (!b?.client) return '—';
    const c = typeof b.client === 'string' ? (b.client && b.client.startsWith('{') ? JSON.parse(b.client) : {}) : b.client;
    return c?.name || c?.label || (typeof b.client === 'string' ? b.client : '—');
  };

  const talentDisplay = (b) => b.talent_name || b.talent_id || b.sql_talent_id || '—';
  const statusDisplay = (b) => (b.status ? String(b.status) : '—');

  if (loading) {
    return (
      <div className="content-wrapper page">
        <div className="content-wrapper">
          <h1>Confirmed bookings (credit notes)</h1>
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="content-wrapper page">
      <div className="content-wrapper">
        <h1>Confirmed bookings (credit notes)</h1>
        <p className="confirmed-cn-intro">
          Create credit notes for confirmed bookings. Select multiple bookings (same talent = one credit note with multiple lines, e.g. same job on different days) or create per booking / for all.
        </p>
        <div className="confirmed-cn-actions">
          <button
            type="button"
            className="cn-btn cn-btn-primary"
            onClick={handleCreateForSelected}
            disabled={createLoading || selectedIds.size === 0}
            title="One credit note per talent; selected bookings from same talent become multiple lines on one PDF/XML"
          >
            {createLoading ? 'Creating…' : `Create credit note for selected (${selectedIds.size})`}
          </button>
          <button
            type="button"
            className="cn-btn cn-btn-secondary"
            onClick={selectAllWithoutNote}
            disabled={withoutNote.length === 0}
          >
            Select all without note
          </button>
          <button type="button" className="cn-btn cn-btn-secondary" onClick={clearSelection} disabled={selectedIds.size === 0}>
            Clear selection
          </button>
          <button
            type="button"
            className="cn-btn cn-btn-primary"
            onClick={handleCreateAllCreditNotes}
            disabled={createLoading || withoutNote.length === 0}
          >
            {createLoading ? 'Creating…' : 'Create credit notes for all'}
          </button>
          <button type="button" className="cn-btn cn-btn-secondary" onClick={() => navigate('/admin/credit-notes')}>
            View all credit notes
          </button>
        </div>
        {createMessage && (
          <div className="confirmed-cn-message success">
            {createMessage}
          </div>
        )}
        {createErrors.length > 0 && (
          <div className="confirmed-cn-message error">
            <strong>Could not create credit note(s) — missing data:</strong>
            <ul style={{ margin: '6px 0 0 0', paddingLeft: '18px' }}>
              {createErrors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        )}
        {error && (
          <div className="confirmed-cn-message error">
            {error}
          </div>
        )}
        <div className="content-wrapper content-wrapper-talent-list">
          <div className="list-tasks-header">
            <div style={{ flexBasis: '60px' }}>Select</div>
            <div style={{ flexBasis: '80px' }}>ID</div>
            <div style={{ flexBasis: '200px' }}>Title</div>
            <div style={{ flexBasis: '120px' }}>Start date</div>
            <div style={{ flexBasis: '150px' }}>Client</div>
            <div style={{ flexBasis: '120px' }}>Status</div>
            <div style={{ flexBasis: '150px' }}>Talent</div>
            <div style={{ flexBasis: '80px' }}></div>
          </div>
        </div>

        {bookings.length === 0 ? (
          <div className="confirmed-cn-empty">No confirmed bookings.</div>
        ) : (
          bookings.map((b) => (
            <div className="list-tasks-item" key={b.id} style={{ paddingLeft: '20px' }}>
              <div style={{ flexBasis: '60px' }}>
                {b.credit_note_generated ? (
                  '—'
                ) : (
                  <input
                    type="checkbox"
                    checked={isSelected(b.id)}
                    onChange={() => toggleSelection(b.id)}
                    aria-label={`Select booking ${b.id}`}
                  />
                )}
              </div>
              <div style={{ flexBasis: '80px' }}>{b.id}</div>
              <div style={{ flexBasis: '200px' }}>{b.title || '—'}</div>
              <div style={{ flexBasis: '120px' }}>{formatDate(b.start_date)}</div>
              <div style={{ flexBasis: '150px' }}>{clientName(b)}</div>
              <div style={{ flexBasis: '120px' }}>{statusDisplay(b)}</div>
              <div style={{ flexBasis: '150px' }}>{talentDisplay(b)}</div>
              <div style={{ flexBasis: '80px' }}>
                {b.credit_note_generated ? (
                  <span className="cell-muted">Created</span>
                ) : (
                  <a
                    className={`edit-button ${createLoading ? 'disabled' : ''}`}
                    onClick={() => !createLoading && handleCreateCreditNote(b)}
                    title="Create credit note"
                    style={{
                      opacity: createLoading ? 0.5 : 1,
                      cursor: createLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <img src={confirmIcon} alt="Create credit note" className="edit-container" />
                  </a>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default ConfirmedListCreditNotes;
